import { computed, Injectable, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
// import { LinkPaymentSheetComponent } from '../components/link-payment-sheet/link-payment-sheet.component';
import { CreatePaymentLinkDto } from '../models/payment-link/create-payment-link.dto';
import {
  PaymentLink,
  PaymentLinkListResponse,
  PaymentLinkStatus
} from '../models/payment-link/payment-link.model';
import { PaymentLinkService } from '../services/payment-link.service';
import { SiteStore } from './site.store';
import { BankAccountsStore } from './bank-accounts.store';

@Injectable({
  providedIn: 'root'
})
export class PaymentLinkStore {
  loading = signal(false);
  loadingDescription = signal('');
  paymentLinks = signal<PaymentLink[]>([]);
  filteredLinks = signal<PaymentLink[]>([]);
  totalLinks = signal(0);
  currentLink = signal<PaymentLink | null>(null);

  // Pagination related signals
  currentPage = signal(1);
  pageSize = signal(10);
  hasMoreLinks = signal(true);
  loadingMore = signal(false);
  triggeredLoad = signal(false);

  paymentLink = signal<any>(undefined);

  // Computed property to check if more links can be loaded
  canLoadMore = computed(() => this.hasMoreLinks() && !this.loading() && !this.loadingMore());

  // Track current search/filter params
  currentFilter = signal<{ name?: string; status?: string }>({});

  constructor(
    private siteStore: SiteStore,
    private bottomSheet: MatBottomSheet,
    private paymentLinkService: PaymentLinkService,
    private matSnackBar: MatSnackBar,
    private bankAccountsStore: BankAccountsStore
  ) {}

  async createPaymentLink(dto: CreatePaymentLinkDto, idempotencyKey?: string) {
    try {
      const currentBankAccount = this.bankAccountsStore.selectedBankAccountId();
      const response = await this.paymentLinkService.createPaymentLink({
        ...dto,
        accountNumber: currentBankAccount
      }, idempotencyKey);
      if (response.data) {
        this.currentLink.set(response.data);
        this.matSnackBar.open('Enlace de pago creado correctamente', 'OK', {
          duration: 3000,
          panelClass: 'mat-snack-success'
        });
        await this.loadPaymentLinks();
        return response.data;
      } else if (response.error) {
        this.matSnackBar.open(response.error?.message || 'Error al crear enlace de pago', 'OK', {
          duration: 3000,
          panelClass: 'mat-snack-error'
        });
      }
    } catch (error) {
      console.error('Error al crear enlace de pago:', error);
      this.matSnackBar.open('Error al crear enlace de pago', 'OK', {
        duration: 3000,
        panelClass: 'mat-snack-error'
      });
    }
    return null;
  }
  async loadPaymentLinks(
    filter?: { name?: string; status?: string },
    pagination: { page?: number; limit?: number } = {},
    reset: boolean = true
  ) {
    const { page, limit } = pagination;
    if (page) this.currentPage.set(page);
    if (limit) this.pageSize.set(limit);

    if (reset) {
      this.loading.set(true);
      this.currentPage.set(1);
      this.paymentLinks.set([]);
      this.hasMoreLinks.set(true);
      this.filteredLinks.set([]);
      this.loadingMore.set(false);
    } else {
      this.loadingMore.set(true);
    }
    try {
      if (filter?.status == 'all') filter.status = null;

      // Store current filter for load more functionality
      if (filter) {
        this.currentFilter.set({ ...filter });
      }

      // Add pagination params to the filter
      const paginatedFilter = {
        ...filter,
        page: this.currentPage(),
        limit: this.pageSize()
      };

      this.triggeredLoad.set(true);
      const currentBankAccount = this.bankAccountsStore.selectedBankAccountId();
      const response = await this.paymentLinkService.getPaymentLinks(
        currentBankAccount,
        paginatedFilter
      );
      if (response.data) {
        const listResponse = response.data as PaymentLinkListResponse;

        // If loading more, append to existing links, otherwise replace
        if (reset) {
          this.paymentLinks.set(listResponse.items);
          this.filteredLinks.set(listResponse.items);
        } else {
          this.paymentLinks.update((links) => [...links, ...listResponse.items]);
          this.filteredLinks.update((links) => [...links, ...listResponse.items]);
        }

        this.totalLinks.set(listResponse.total);

        // Check if we have more links to load
        this.hasMoreLinks.set(this.paymentLinks().length < listResponse.total);

        return listResponse;
      } else if (response.error) {
        this.matSnackBar.open(
          response.error?.message || 'Error al cargar enlaces de pago',
          'Cerrar',
          {
            duration: 3000,
            panelClass: 'mat-snack-error'
          }
        );
      }
    } catch (error) {
      console.error('Error al cargar enlaces de pago:', error);
      this.matSnackBar.open('Error al cargar enlaces de pago', 'Cerrar', {
        duration: 3000,
        panelClass: 'mat-snack-error'
      });
    } finally {
      if (reset) {
        this.loading.set(false);
      } else {
        this.loadingMore.set(false);
      }
    }
    return null;
  }
  async filterLinks(searchTerm: string, statusFilter: string | null = null, reset: boolean = true) {
    // Create filter object for API call
    const filter: { name?: string; status?: string } = {};

    if (searchTerm && searchTerm.trim() !== '') {
      filter.name = searchTerm.trim();
    }

    if (statusFilter) {
      filter.status = statusFilter;
    }

    return this.loadPaymentLinks(filter, {}, reset);
  }

  async loadMoreLinks() {
    if (!this.canLoadMore()) {
      return;
    }

    // Increment the page number
    const page = this.currentPage();
    const pagination = { page: page + 1 };
    this.loadPaymentLinks(this.currentFilter(), pagination, false);
  }
  async removePaymentLink(id: string) {
    this.loading.set(true); // Show loading state

    try {
      const response = await this.paymentLinkService.removePaymentLink(id);
      if (response.data?.success) {
        this.matSnackBar.open('Enlace de pago eliminado correctamente', 'Cerrar', {
          duration: 3000,
          panelClass: 'mat-snack-success'
        });

        // Reload using current filter and reset to first page
        await this.loadPaymentLinks(this.currentFilter(), {}, true);
        return true;
      } else if (response.error) {
        this.matSnackBar.open(
          response.error?.message || 'Error al eliminar enlace de pago',
          'Cerrar',
          {
            duration: 3000,
            panelClass: 'mat-snack-error'
          }
        );
      }
    } catch (error) {
      console.error('Error al eliminar enlace de pago:', error);
      this.matSnackBar.open('Error al eliminar enlace de pago', 'Cerrar', {
        duration: 3000,
        panelClass: 'mat-snack-error'
      });
    } finally {
      this.loading.set(false); // Hide loading state
    }
    return false;
  }

  async manageLinkPayment(token: string) {
    this.siteStore.setLoading('Cargando enlace de pago...');

    try {
      const res = await this.paymentLinkService.getPaymentLinkByToken(token);
      this.siteStore.removeLoading();

      if (res.data) {
        const paymentLink = res.data;
        // if (String(paymentLink.userId) === String(this.authStore.user().id)) {
        // 	this.matSnackBar.open('No puede pagar su propio enlace', 'Ok', {
        // 		duration: 5000,
        // 		panelClass: 'mat-snack-error'
        // 	});
        // 	return;
        // }
        this.paymentLink.set(paymentLink);

        // Check if the payment link has expired
        if (paymentLink.expirationDate && new Date(paymentLink.expirationDate) < new Date()) {
          this.matSnackBar.open('El enlace de pago ha expirado', 'Ok', {
            duration: 5000,
            panelClass: 'mat-snack-error'
          });
          return;
        }

        // Check if the payment link has been used (for single use links)
        if (paymentLink.status === PaymentLinkStatus.USED) {
          this.matSnackBar.open('El enlace de pago ya ha sido utilizado', 'Ok', {
            duration: 5000,
            panelClass: 'mat-snack-error'
          });
          return;
        }

        // Open the link payment sheet component
        // const ref = this.bottomSheet.open(LinkPaymentSheetComponent, {
        //   data: { paymentLink },
        //   panelClass: 'app-sheet-container'
        // });

        // ref.afterDismissed().subscribe(async () => {
        //   // await this.transferStore.reloadTransactions();
        // });
      } else {
        const message =
          res.error?.errors?.[0]?.message ??
          res.error?.errors?.message ??
          'Enlace de pago inválido';

        this.matSnackBar.open(message, 'Ok', {
          duration: 4000,
          panelClass: 'mat-snack-error'
        });
      }
    } catch (error) {
      this.siteStore.removeLoading();
      this.matSnackBar.open('Error al cargar el enlace de pago', 'Ok', {
        duration: 4000,
        panelClass: 'mat-snack-error'
      });

      if (environment.env !== 'production') {
        console.error('Payment link error:', error);
      }
    } finally {
      this.siteStore.removeLoading();
    }
  }

  /**
   * Clears all payment link data from the store
   * Used when a user logs out to ensure no data remains in memory
   */
  clearStore() {
    this.loading.set(false);
    this.loadingMore.set(false);
    this.loadingDescription.set('');
    this.paymentLinks.set([]);
    this.filteredLinks.set([]);
    this.totalLinks.set(0);
    this.currentLink.set(null);
    this.currentPage.set(1);
    this.hasMoreLinks.set(true);
    this.currentFilter.set({});
  }
}
